import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Hospital, Search, MapPin, Phone, FileText, Edit2,
  Users, Calendar, Shield, Loader2, ArrowUpDown, ChevronLeft, ChevronRight,
  Smartphone,
} from "lucide-react";
import { formatPhone, formatCNPJ, formatCEP } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import {
  Clinic, CreateClinicData, fetchClinics, updateClinic,
  fetchClinicInsurances, associateClinicInsurance, disassociateClinicInsurance,
} from "@/services/clinic-service";
import { fetchInsurances } from "@/services/insurance-service";
import { fetchHolidays, Holiday } from "@/services/holiday-service";
import { User, fetchUsersByClinic } from "@/services/user-service";
import { CreateEditClinicDialog } from "@/components/clinics/CreateEditClinicDialog";
import { useClinic } from "@/contexts/ClinicContext";

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  company_admin: "Admin da Empresa",
  clinic_manager: "Gerente de Clínica",
  receptionist: "Recepcionista",
  insurance_billing: "Faturamento Convênio",
};

const ROLE_COLORS: Record<string, string> = {
  super_admin:       "bg-accent/12 text-accent border-accent/30",
  company_admin:     "bg-accent/12 text-accent border-accent/30",
  clinic_manager:    "bg-primary/12 text-primary border-primary/30",
  receptionist:      "bg-[hsl(32,45%,90%)] text-[hsl(28,50%,22%)] border-[hsl(32,55%,58%)]/40",
  insurance_billing: "bg-[hsl(280,22%,92%)] text-[hsl(280,35%,25%)] border-[hsl(280,28%,62%)]/40",
};

const SCOPE_LABELS: Record<string, string> = {
  NATIONAL: "Nacional",
  STATE: "Estadual",
  MUNICIPAL: "Municipal",
  RECESS: "Recesso",
  CLINIC_RECESS: "Recesso Clínica",
};

const SCOPE_COLORS: Record<string, string> = {
  NATIONAL:      "bg-[hsl(220,30%,92%)] text-[hsl(220,40%,25%)] border-[hsl(220,35%,60%)]/40",
  STATE:         "bg-[hsl(32,45%,90%)] text-[hsl(28,50%,22%)] border-[hsl(32,55%,58%)]/40",
  MUNICIPAL:     "bg-[hsl(158,28%,88%)] text-[hsl(158,40%,20%)] border-[hsl(158,34%,55%)]/40",
  RECESS:        "bg-[hsl(12,55%,92%)] text-[hsl(8,55%,28%)] border-[hsl(12,60%,65%)]/40",
  CLINIC_RECESS: "bg-[hsl(280,22%,92%)] text-[hsl(280,35%,25%)] border-[hsl(280,28%,62%)]/40",
};

interface PlanRow {
  id: string;
  name: string;
  active: boolean;
}

// Badge de contagem — mostra "—" enquanto carrega
function CountBadge({ value, loading }: { value: number; loading: boolean }) {
  return (
    <Badge variant="secondary" className="text-[9px] px-1 py-0 ml-1 tabular-nums">
      {loading ? "—" : value}
    </Badge>
  );
}

export default function ClinicDetail() {
  const { toast } = useToast();
  const { selectedClinicId: globalClinicId, setSelectedClinicId: setGlobalClinicId } = useClinic();

  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // Tab data
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [togglingPlan, setTogglingPlan] = useState<string | null>(null);

  // Plans view
  const [showOnlyActivePlans, setShowOnlyActivePlans] = useState(true);

  // Holidays view
  const [holidaySortOrder, setHolidaySortOrder] = useState<"asc" | "desc">("asc");
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  const [showPastHolidays, setShowPastHolidays] = useState(true);

  // Edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const selectedClinic = clinics.find(c => c.id === globalClinicId) || null;

  // ── Load clinics ──
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchClinics(undefined, true, true);
        setClinics(data);
        // Se não há clínica selecionada globalmente, seleciona a primeira
        if (!globalClinicId && data.length > 0) {
          setGlobalClinicId(data[0].id);
        }
      } catch {
        toast({ title: "Erro ao carregar clínicas", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load tab data when clinic changes ──
  const loadPlans = useCallback(async (clinicId: string) => {
    setLoadingPlans(true);
    try {
      const [allInsurances, clinicInsurances] = await Promise.all([
        fetchInsurances(),
        fetchClinicInsurances(clinicId),
      ]);
      const clinicInsuranceIds = new Set(clinicInsurances.map(ci => ci.id));
      setPlans(
        allInsurances.map(ins => ({
          id: ins.id,
          name: ins.name,
          active: clinicInsuranceIds.has(ins.id),
        }))
      );
    } catch {
      toast({ title: "Erro ao carregar planos", variant: "destructive" });
    } finally {
      setLoadingPlans(false);
    }
  }, [toast]);

  const loadHolidays = useCallback(async (clinic: Clinic, year: number) => {
    setLoadingHolidays(true);
    try {
      const data = await fetchHolidays(undefined, undefined, undefined, year);
      const relevant = data.filter(h => {
        if (h.scope === "NATIONAL" || h.scope === "RECESS") return true;
        if (h.scope === "STATE" && h.state_code === clinic.state_code) return true;
        if (h.scope === "MUNICIPAL" && h.city_name === clinic.city_name && h.state_code === clinic.state_code) return true;
        if (h.scope === "CLINIC_RECESS" && h.clinic_id === clinic.id) return true;
        return false;
      });
      setHolidays(relevant);
    } catch {
      toast({ title: "Erro ao carregar feriados", variant: "destructive" });
    } finally {
      setLoadingHolidays(false);
    }
  }, [toast]);

  const loadUsers = useCallback(async (clinicId: string) => {
    setLoadingUsers(true);
    try {
      const data = await fetchUsersByClinic(clinicId);
      setUsers(data);
    } catch {
      toast({ title: "Erro ao carregar usuários", variant: "destructive" });
    } finally {
      setLoadingUsers(false);
    }
  }, [toast]);

  // Carrega usuários e planos uma única vez (quando a clínica muda)
  useEffect(() => {
    if (!globalClinicId || !selectedClinic) return;
    loadPlans(globalClinicId);
    loadUsers(globalClinicId);
  }, [globalClinicId, selectedClinic, loadPlans, loadUsers]);

  // Carrega feriados quando o ano muda
  useEffect(() => {
    if (!selectedClinic) return;
    loadHolidays(selectedClinic, holidayYear);
  }, [holidayYear, selectedClinic, loadHolidays]);

  // ── Toggle plan (confirma ao desativar) ──
  const togglePlan = async (planId: string) => {
    if (!globalClinicId) return;
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    if (plan.active) {
      const ok = window.confirm(
        `Desativar "${plan.name}" para esta clínica?\n\nAtendimentos já registrados não serão afetados.`
      );
      if (!ok) return;
    }

    setTogglingPlan(planId);
    try {
      if (plan.active) {
        await disassociateClinicInsurance(globalClinicId, planId);
      } else {
        await associateClinicInsurance(globalClinicId, planId);
      }
      setPlans(prev => prev.map(p => p.id === planId ? { ...p, active: !p.active } : p));
    } catch (e: any) {
      toast({ title: e.message || "Erro ao atualizar plano", variant: "destructive" });
    } finally {
      setTogglingPlan(null);
    }
  };

  // ── Edit clinic ──
  const handleSave = async (data: CreateClinicData, id?: string) => {
    if (!id) return;
    try {
      await updateClinic(id, data);
      toast({ title: "Clínica atualizada!" });
      const refreshed = await fetchClinics(undefined, true, true);
      setClinics(refreshed);
    } catch (e: any) {
      toast({ title: e.message || "Erro ao atualizar", variant: "destructive" });
      throw e;
    }
  };

  // ── Derived ──
  const filteredClinics = clinics.filter(c =>
    (c.trade_name || c.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activePlansCount = plans.filter(p => p.active).length;
  const displayedPlans = showOnlyActivePlans ? plans.filter(p => p.active) : plans;

  // Feriados: filtra passados + ordena
  const todayIso = new Date().toISOString().slice(0, 10);
  const visibleHolidays = holidays.filter(h => showPastHolidays || h.holiday_date >= todayIso);
  const sortedHolidays = [...visibleHolidays].sort((a, b) => {
    const comparison = a.holiday_date.localeCompare(b.holiday_date);
    return holidaySortOrder === "asc" ? comparison : -comparison;
  });
  const pastCount = holidays.length - visibleHolidays.length;

  if (loading) {
    return (
      <div className="px-4 py-4">
        <div className="flex gap-4">
          <div className="w-72 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-12 rounded-md bg-muted/40 animate-pulse" />
            ))}
          </div>
          <div className="flex-1 space-y-3">
            <div className="h-20 rounded-md bg-muted/40 animate-pulse" />
            <div className="h-9 rounded-md bg-muted/40 animate-pulse" />
            <div className="h-64 rounded-md bg-muted/40 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 flex flex-col h-full min-h-0">
      {/* Header editorial */}
      <div className="mb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Hospital className="h-5 w-5 text-primary" />
          <h1 className="font-display text-2xl font-medium tracking-tight text-foreground">Clínicas</h1>
        </div>
        <p className="font-display italic text-sm text-muted-foreground mt-0.5">
          Gerencie cadastros, planos, feriados e usuários.
        </p>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* ── Sidebar ── */}
        <div className="w-72 flex-shrink-0 flex flex-col border border-border rounded-lg bg-card overflow-hidden">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar clínica..."
                className="h-8 text-xs pl-8"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <p className="mt-2 font-display text-[10px] italic uppercase tracking-[0.18em] text-muted-foreground">
              {filteredClinics.length} {filteredClinics.length === 1 ? "clínica" : "clínicas"}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-border/60">
            {filteredClinics.length === 0 ? (
              <div className="p-6 text-center">
                <p className="font-display italic text-xs text-muted-foreground">
                  {searchTerm ? "Nenhuma clínica encontrada." : "Nenhuma clínica cadastrada."}
                </p>
              </div>
            ) : (
              filteredClinics.map(clinic => {
                const isSelected = clinic.id === globalClinicId;
                const isActive = clinic.status === 1;
                const hasWhatsapp = Boolean((clinic as any).whapi_phone);
                return (
                  <button
                    key={clinic.id}
                    onClick={() => setGlobalClinicId(clinic.id)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 transition-colors relative",
                      isSelected ? "bg-primary/[0.08] hover:bg-primary/[0.1]" : "hover:bg-muted/40"
                    )}
                  >
                    {isSelected && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary" />}
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "w-1.5 h-1.5 rounded-full shrink-0",
                          isActive ? "bg-[hsl(var(--success))]" : "bg-muted-foreground/40"
                        )}
                        title={isActive ? "Ativa" : "Inativa"}
                      />
                      <span className={cn("text-sm truncate flex-1", isSelected ? "font-semibold" : "font-medium")}>
                        {clinic.trade_name || clinic.name}
                      </span>
                      {hasWhatsapp && (
                        <Smartphone className="h-3 w-3 text-[hsl(var(--success))] shrink-0" aria-label="WhatsApp configurado" />
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground ml-3.5 font-display italic">
                      {clinic.city_name}/{clinic.state_code}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Main ── */}
        {selectedClinic ? (
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
            {/* Card de identificação — agora com todos os dados */}
            <Card className="mb-4 flex-shrink-0">
              <CardContent className="py-4 px-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-base font-bold truncate">{selectedClinic.trade_name || selectedClinic.name}</h2>
                      <Badge variant={selectedClinic.status === 1 ? "default" : "secondary"} className="text-[10px]">
                        {selectedClinic.status === 1 ? "Ativa" : "Inativa"}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-3 text-xs">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <FileText className="h-3 w-3 shrink-0" />
                        <span className="shrink-0">CNPJ:</span>
                        <span className="text-foreground font-medium tabular-nums">{formatCNPJ(selectedClinic.cnpj)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="shrink-0">Telefone:</span>
                        <span className="text-foreground font-medium tabular-nums">{formatPhone(selectedClinic.phone)}</span>
                      </div>
                      {selectedClinic.cellphone && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Smartphone className="h-3 w-3 shrink-0" />
                          <span className="shrink-0">Celular:</span>
                          <span className="text-foreground font-medium tabular-nums">{formatPhone(selectedClinic.cellphone)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="shrink-0">CEP:</span>
                        <span className="text-foreground font-medium tabular-nums">{formatCEP(selectedClinic.postcode)}</span>
                      </div>
                      <div className="flex items-start gap-1.5 text-muted-foreground md:col-span-2">
                        <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                        <span className="shrink-0">Endereço:</span>
                        <span className="text-foreground font-medium">
                          {selectedClinic.address}
                          {selectedClinic.additional_address ? `, ${selectedClinic.additional_address}` : ""}
                          {" — "}{selectedClinic.city_name}/{selectedClinic.state_code}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs shrink-0" onClick={() => setIsEditDialogOpen(true)}>
                    <Edit2 className="h-3 w-3 mr-1" /> Editar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tabs — sem mais aba "Dados" */}
            <Tabs defaultValue="plans" className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
                <TabsTrigger value="plans" className="text-xs gap-1">
                  <Shield className="h-3 w-3" /> Planos
                  <CountBadge value={activePlansCount} loading={loadingPlans} />
                </TabsTrigger>
                <TabsTrigger value="holidays" className="text-xs gap-1">
                  <Calendar className="h-3 w-3" /> Feriados
                  <CountBadge value={holidays.length} loading={loadingHolidays} />
                </TabsTrigger>
                <TabsTrigger value="users" className="text-xs gap-1">
                  <Users className="h-3 w-3" /> Usuários
                  <CountBadge value={users.length} loading={loadingUsers} />
                </TabsTrigger>
              </TabsList>

              {/* ── Tab: Planos ── */}
              <TabsContent value="plans" className="flex-1 data-[state=active]:flex data-[state=active]:flex-col min-h-0 mt-3">
                <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="flex-shrink-0">
                    <div className="px-5 py-3 border-b flex items-center justify-between">
                      <p className="text-xs">
                        <span className="tabular-nums font-semibold text-foreground">{activePlansCount}</span>
                        <span className="text-muted-foreground"> de </span>
                        <span className="tabular-nums font-semibold text-foreground">{plans.length}</span>
                        <span className="text-muted-foreground"> planos ativos</span>
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Somente ativos</span>
                        <Switch
                          checked={showOnlyActivePlans}
                          onCheckedChange={setShowOnlyActivePlans}
                        />
                      </div>
                    </div>
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow>
                          <TableHead className="text-xs h-9">Plano / Convênio</TableHead>
                          <TableHead className="text-xs w-24 text-center h-9">Status</TableHead>
                          <TableHead className="text-xs w-20 text-center h-9">Ativo</TableHead>
                        </TableRow>
                      </TableHeader>
                    </Table>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {loadingPlans ? (
                      <div className="p-3 space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="h-8 rounded bg-muted/40 animate-pulse" />
                        ))}
                      </div>
                    ) : displayedPlans.length === 0 ? (
                      <div className="py-10 text-center text-sm text-muted-foreground">
                        {showOnlyActivePlans ? "Nenhum plano ativo nesta clínica." : "Nenhum plano disponível."}
                      </div>
                    ) : (
                      <Table>
                        <TableBody>
                          {displayedPlans.map(plan => (
                            <TableRow key={plan.id} className={!plan.active ? "opacity-60" : ""}>
                              <TableCell className="text-sm font-medium">{plan.name}</TableCell>
                              <TableCell className="text-center w-24">
                                <Badge variant={plan.active ? "default" : "secondary"} className="text-[10px]">
                                  {plan.active ? "Ativo" : "Inativo"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center w-20">
                                <Switch
                                  checked={plan.active}
                                  onCheckedChange={() => togglePlan(plan.id)}
                                  disabled={togglingPlan === plan.id}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </Card>
              </TabsContent>

              {/* ── Tab: Feriados ── */}
              <TabsContent value="holidays" className="flex-1 data-[state=active]:flex data-[state=active]:flex-col min-h-0 mt-3">
                <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-between px-5 py-3 border-b gap-3">
                      <p className="text-xs text-muted-foreground">
                        Feriados que afetam esta clínica
                      </p>
                      <div className="flex items-center gap-3">
                        {/* Seletor de ano */}
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => setHolidayYear(y => y - 1)}
                            title="Ano anterior"
                          >
                            <ChevronLeft className="h-3 w-3" />
                          </Button>
                          <span className="text-xs font-semibold tabular-nums w-10 text-center">{holidayYear}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => setHolidayYear(y => y + 1)}
                            title="Próximo ano"
                          >
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            Mostrar passados
                            {!showPastHolidays && pastCount > 0 && (
                              <span className="text-muted-foreground/70"> ({pastCount})</span>
                            )}
                          </span>
                          <Switch
                            checked={showPastHolidays}
                            onCheckedChange={setShowPastHolidays}
                          />
                        </div>
                      </div>
                    </div>
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow>
                          <TableHead
                            className="text-xs w-32 h-9 cursor-pointer hover:text-primary transition-colors"
                            onClick={() => setHolidaySortOrder(prev => prev === "asc" ? "desc" : "asc")}
                          >
                            <div className="flex items-center gap-1">
                              Data <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead className="text-xs h-9">Nome</TableHead>
                          <TableHead className="text-xs w-32 h-9">Escopo</TableHead>
                        </TableRow>
                      </TableHeader>
                    </Table>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {loadingHolidays ? (
                      <div className="p-3 space-y-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="h-8 rounded bg-muted/40 animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      <Table>
                        <TableBody>
                          {sortedHolidays.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">
                                {holidays.length > 0
                                  ? "Nenhum feriado futuro. Ative \"Mostrar passados\" para ver o histórico."
                                  : "Nenhum feriado encontrado para este ano."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            sortedHolidays.map(h => {
                              const isPast = h.holiday_date < todayIso;
                              return (
                                <TableRow key={h.id} className={isPast ? "opacity-50" : ""}>
                                  <TableCell className="text-xs font-mono w-32">
                                    {(() => { const [,m,d] = h.holiday_date.split("-"); return `${d}/${m}`; })()} ({new Date(h.holiday_date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "short" })})
                                  </TableCell>
                                  <TableCell className="text-sm">{h.name}</TableCell>
                                  <TableCell className="w-32">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                      SCOPE_COLORS[h.scope] || "bg-gray-100 text-gray-700"
                                    }`}>
                                      {SCOPE_LABELS[h.scope] || h.scope}
                                    </span>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </Card>
              </TabsContent>

              {/* ── Tab: Usuários ── */}
              <TabsContent value="users" className="flex-1 data-[state=active]:flex data-[state=active]:flex-col min-h-0 mt-3">
                <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="flex-shrink-0">
                    <div className="px-5 py-3 border-b mt-3">
                      <p className="text-xs text-muted-foreground">
                        <span className="tabular-nums font-semibold text-foreground">{users.length}</span> usuário(s) no sistema
                      </p>
                    </div>
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow>
                          <TableHead className="text-xs h-9">Nome</TableHead>
                          <TableHead className="text-xs h-9">Email</TableHead>
                          <TableHead className="text-xs w-32 h-9">Perfil</TableHead>
                        </TableRow>
                      </TableHeader>
                    </Table>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {loadingUsers ? (
                      <div className="p-3 space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="h-8 rounded bg-muted/40 animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      <Table>
                        <TableBody>
                          {users.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">
                                Nenhum usuário encontrado.
                              </TableCell>
                            </TableRow>
                          ) : (
                            users.map(u => (
                              <TableRow key={u.id}>
                                <TableCell className="text-sm font-medium">{u.name}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                                <TableCell className="w-32">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                    ROLE_COLORS[u.role] || "bg-gray-100 text-gray-700"
                                  }`}>
                                    {ROLE_LABELS[u.role] || u.role}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Edit Dialog */}
            <CreateEditClinicDialog
              open={isEditDialogOpen}
              onOpenChange={setIsEditDialogOpen}
              clinic={selectedClinic}
              onSave={handleSave}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            Selecione uma clínica
          </div>
        )}
      </div>
    </div>
  );
}
